/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AuthSettingsModule
 */

import {
    Component,
    Input,
    OnInit,
} from '@angular/core';

import {
    AuthMappingProfileItem,
    AuthMappingRuleConfigItem,
} from 'ajs/modules/auth-settings/factories';

import {
    RoleFilterConfigItem,
} from 'ajs/modules/accounts/factories/role/role-filter.config-item.factory';

import { AuthProfileType } from 'generated-types';
import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';
import { IRowReorder } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import * as globalL10n from 'global-l10n';
import * as l10n from './auth-mapping-profile-modal.l10n';

const { ENGLISH, ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Modal Component for AuthMappingProfile Configuration.
 *
 * @author Aravindh Nagarajan
 */
@Component({
    selector: 'auth-mapping-profile-modal',
    templateUrl: './auth-mapping-profile-modal.component.html',
})
export class AuthMappingProfileModalComponent implements OnInit {
    /**
     * AuthMappingProfile ObjectTypeItem instance.
     */
    @Input()
    public editable: AuthMappingProfileItem;

    /**
     * Layout for clrForm.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * For template usage.
     */
    public readonly l10nKeys = l10nKeys;
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * ObjectType of this.editable.
     */
    public objectType: string;

    /**
     * Array of hidden auth profile types in the dropdown.
     */
    public readonly hiddenAuthProfileTypes = [AuthProfileType.AUTH_PROFILE_CLIENT_CERT];

    constructor(
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.objectType = this.editable.messageType;
    }

    /**
     * Adds a mapping rule.
     */
    public addAuthMappingRule(): void {
        this.editable.addAuthMappingRule(this.isLdapProfile);
    }

    /**
     * Edits a mapping rule.
     */
    public editAuthMappingRule(mappingRule: AuthMappingRuleConfigItem): void {
        this.editable.editAuthMappingRule(mappingRule, this.isLdapProfile);
    }

    /**
     * Deletes a mapping rule.
     */
    public deleteAuthMappingRule(mappingRule: AuthMappingRuleConfigItem): void {
        this.editable.mappingRules.removeByMessageItem(mappingRule);
    }

    /**
     * Handle Row reorder event emitted from grid.
     * Update array index as well as `index` property.
     */
    public handleAuthMappingRuleGridRowReorder({
        previousIndex,
        currentIndex,
    }: IRowReorder): void {
        this.editable.mappingRules.moveByIndex(previousIndex, currentIndex);
    }

    /**
     * Handler for Role Filter grid add operation.
     */
    public onAddRoleFilter(): void {
        this.editable.addDynamicRoleFilter();
    }

    /**
     * Handler for Role Filter grid remove operation.
     */
    public onRemoveRoleFilter(labelFilter: RoleFilterConfigItem): void {
        this.editable.removeDynamicRoleFilter(labelFilter);
    }

    /**
     * Handler for Role Filter grid edit operation.
     */
    public onEditRoleFilter(labelFilter: RoleFilterConfigItem): void {
        this.editable.editDynamicRoleFilter(labelFilter);
    }

    /**
     * Returns true if profile type is ldap.
     */
    private get isLdapProfile(): boolean {
        return this.editable.type === AuthProfileType.AUTH_PROFILE_LDAP;
    }
}
